import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import {
  Trash,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import "react-toastify/dist/ReactToastify.css";
import "../css/LoanPreClosure.css";

const API_URL = "https://payyug.in/api/";

const LoanPreClosure = () => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("");
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    loanId: null,
    type: "",
  });

  const fetchLoans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}loan-reports`);
      setLoans(response.data.loans || []);
    } catch (err) {
      // Dummy data for testing
      // setLoans([
      //   {
      //     id: 1,
      //     userName: "Aman Verma",
      //     loanAccountNumber: "PY-9901",
      //     loanAmount: 25000,
      //     status: "Pending",
      //     loanType: "Personal",
      //   },
      //   {
      //     id: 2,
      //     userName: "Sita Kumari",
      //     loanAccountNumber: "PY-9902",
      //     loanAmount: 50000,
      //     status: "Approved",
      //     loanType: "Business",
      //   },
      //   {
      //     id: 3,
      //     userName: "Vikram Singh",
      //     loanAccountNumber: "PY-9903",
      //     loanAmount: 15000,
      //     status: "Rejected",
      //     loanType: "Personal",
      //   },
      // ]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  // Open the modal
  const openModal = (id, type) => {
    setModalConfig({ isOpen: true, loanId: id, type });
  };

  // Close the modal
  const closeModal = () => {
    setModalConfig({ isOpen: false, loanId: null, type: "" });
  };

  // Execute the API call
  const confirmAction = async () => {
    const { loanId, type } = modalConfig;
    try {
      const endpoint =
        type === "delete"
          ? `delete-loan/${loanId}`
          : `update-loan/${loanId}/${type}`;
      await axios.post(`${API_URL}${endpoint}`);
      toast.success(`Loan ${type}ed successfully!`);
      fetchLoans();
    } catch (err) {
      toast.error(`Failed to ${type} loan`);
    } finally {
      closeModal();
    }
  };

  // Filter & Search Logic
  const filteredLoans = loans.filter((loan) => {
    const matchesSearch =
      loan.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.loanAccountNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter ? loan.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredLoans.length / itemsPerPage);
  const currentData = filteredLoans.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="loan-pre-closer-wrapper">
      <header className="loan-pre-closer-header">
        <div className="loan-pre-closer-title-area">
          <h1 className="loan-pre-closer-main-title">Loan Pre-closure Management</h1>
          {/* <p className="loan-pre-closer-tagline">Track and process all loan applications in one place.</p> */}
        </div>
      </header>

      <section className="loan-pre-closer-toolbar">
        <div className="loan-pre-closer-search-container">
          {/* <Search className="loan-pre-closer-search-icon" size={20} /> */}
          <input
            className="loan-pre-closer-input"
            type="text"
            placeholder="Search borrower or account no..."
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="loan-pre-closer-filter-box">
          <Filter size={18} />
          <select
            className="loan-pre-closer-select"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>

          <select
            className="loan-pre-closer-select"
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
          >
            <option value="10">Show 10</option>
            <option value="25">Show 25</option>
            <option value="50">Show 50</option>
          </select>
        </div>
      </section>

      <div className="loan-pre-closer-table-card">
        <table className="loan-pre-closer-table">
          <thead>
            <tr>
              <th>A/C NO</th>
              <th>BORROWER</th>
              <th>AMOUNT</th>
              <th>TYPE</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="loan-pre-closer-loading">
                  <Loader2 className="loan-pre-closer-spinner" /> Loading...
                </td>
              </tr>
            ) : currentData.length > 0 ? (
              currentData.map((loan) => (
                <tr key={loan.id} className="loan-pre-closer-row">
                  <td className="loan-pre-closer-bold">{loan.loanAccountNumber}</td>
                  <td>{loan.userName}</td>
                  <td>₹{loan.loanAmount.toLocaleString()}</td>
                  <td>{loan.loanType}</td>
                  <td>
                    <span
                      className={`loan-pre-closer-badge loan-pre-closer-badge-${loan.status.toLowerCase()}`}
                    >
                      {loan.status}
                    </span>
                  </td>
                  <td className="loan-pre-closer-actions">
                    {loan.status === "Pending" && (
                      <>
                        <button
                          title="Approve"
                          className="loan-pre-closer-btn loan-pre-closer-btn-approve"
                          onClick={() => openModal(loan.id, "approve")}
                        >
                          <CheckCircle size={18} />
                        </button>

                        <button
                          title="Reject"
                          className="loan-pre-closer-btn loan-pre-closer-btn-reject"
                          onClick={() => openModal(loan.id, "reject")}
                        >
                          <XCircle size={18} />
                        </button>
                      </>
                    )}
                    {/* <button title="Delete" className="loan-pre-closer-btn loan-pre-closer-btn-delete" onClick={() => handleAction(loan.id, 'delete')}>
                      <Trash size={18} />
                    </button> */}

                    <button
                      title="Delete"
                      className="loan-pre-closer-btn loan-pre-closer-btn-delete"
                      onClick={() => openModal(loan.id, "delete")}
                    >
                      <Trash size={18} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="loan-pre-closer-empty">
                  No loan records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <footer className="loan-pre-closer-pagination">
        <button
          className="loan-pre-closer-page-btn"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => p - 1)}
        >
          <ChevronLeft size={18} />
        </button>
        <span className="loan-pre-closer-page-info">
          Page <b>{currentPage}</b> of <b>{totalPages || 1}</b>
        </span>
        <button
          className="loan-pre-closer-page-btn"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => p + 1)}
        >
          <ChevronRight size={18} />
        </button>
      </footer>

      {/* --- ACTION MODAL --- */}
      {modalConfig.isOpen && (
        <div className="loan-pre-closer-modal-overlay">
          <div className="loan-pre-closer-modal-content">
            <div className={`loan-pre-closer-modal-icon icon-${modalConfig.type}`}>
              {modalConfig.type === "delete" && <Trash size={40} />}
              {modalConfig.type === "approve" && <CheckCircle size={40} />}
              {modalConfig.type === "reject" && <XCircle size={40} />}
            </div>
            <h2>
              Confirm{" "}
              {modalConfig.type.charAt(0).toUpperCase() +
                modalConfig.type.slice(1)}
            </h2>
            <p>
              Are you sure you want to <b>{modalConfig.type}</b> this loan
              record? This action might be irreversible.
            </p>

            <div className="loan-pre-closer-modal-actions">
              <button className="loan-pre-closer-btn-cancel" onClick={closeModal}>
                Cancel
              </button>
              <button
                className={`loan-pre-closer-btn-confirm btn-${modalConfig.type}`}
                onClick={confirmAction}
              >
                Confirm {modalConfig.type}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default LoanPreClosure;
